import React from "react";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import StarIcon from "@mui/icons-material/Star";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import {
  CardChip,
  CardColors,
  CardStyle,
  CardTagline,
  LIGHT_CARD_COLORS,
  TaglineFont,
} from "../../../../service/BuyerConfiguration/BuyerAppDashboard/restaurantCardsService";
import { CardIcon } from "./cardIcons";

/** What one card shows (the restaurant's own data plus its card extras). */
export type CardPreviewData = {
  name: string;
  rating: number;
  reviews: string;
  distance: string;
  area: string;
  cuisines: string;
  /** Cover first, then rolling photos. Empty = food placeholder. */
  photos: string[];
  badge: { text: string; color: string; icon: string } | null;
  tagline: CardTagline | null;
  chips: CardChip[];
  time: string;
};

export const SAMPLE_CARD: CardPreviewData = {
  name: "AL - BAIK",
  rating: 4.3,
  reviews: "1.2K",
  distance: "0.75 Km",
  area: "Poonamallee",
  cuisines: "BBQ, Grill, Chinese",
  photos: [],
  badge: { text: "Popular", color: "#E53935", icon: "local-fire-department" },
  tagline: { text: "Freshly Grilled", font: "script", color: "#FFFFFF", x: 0.78, y: 0.45, size: 1, rotate: -8 },
  chips: [
    { key: "hygienic", label: "Hygienic", icon: "verified-user" },
    { key: "great-taste", label: "Great Taste", icon: "thumb-up" },
    { key: "budget", label: "Budget Friendly", icon: "currency-rupee" },
  ],
  time: "25-30 mins",
};

/** Three sample cards for the "look for all cards" preview. */
export const SAMPLE_CARDS: CardPreviewData[] = [
  SAMPLE_CARD,
  {
    name: "Biryani Junction",
    rating: 4.5,
    reviews: "860",
    distance: "1.4 Km",
    area: "Porur",
    cuisines: "Biryani, Mughlai, North Indian",
    photos: [],
    badge: { text: "Flat 20% OFF", color: "#F4511E", icon: "local-offer" },
    tagline: {
      text: "Authentic Biryani Experience",
      font: "script",
      color: "#FFE082",
      x: 0.74,
      y: 0.3,
      size: 0.9,
      rotate: -6,
    },
    chips: [
      { key: "fresh", label: "Fresh Ingredients", icon: "eco" },
      { key: "family-pack", label: "Family Packs", icon: "groups" },
    ],
    time: "30-35 mins",
  },
  {
    name: "Green Leaf Kitchen",
    rating: 4.1,
    reviews: "312",
    distance: "2.1 Km",
    area: "Valasaravakkam",
    cuisines: "South Indian, Thali",
    photos: [],
    badge: { text: "Pure Veg", color: "#2E7D32", icon: "spa" },
    tagline: null,
    chips: [
      { key: "pure-veg", label: "Pure Veg", icon: "spa" },
      { key: "quick-delivery", label: "Quick Delivery", icon: "delivery-dining" },
    ],
    time: "20-25 mins",
  },
];

/** Placeholder "food photo" when a card has none. */
const FOOD_PLACEHOLDER = [
  "radial-gradient(circle at 74% 52%, rgba(255,214,140,0.95) 0 9%, transparent 10%)",
  "radial-gradient(circle at 63% 40%, rgba(196,74,26,0.95) 0 11%, transparent 12%)",
  "radial-gradient(circle at 84% 34%, rgba(122,160,52,0.9) 0 6%, transparent 7%)",
  "radial-gradient(circle at 72% 50%, #8a3a14 0 26%, transparent 27%)",
  "radial-gradient(circle at 72% 50%, #f2e3cf 0 31%, transparent 32%)",
  "linear-gradient(135deg, #3b2216 0%, #6b3a1f 45%, #a45a2a 100%)",
].join(", ");

const CREAM = "#FFF6EE";
const ORANGE = "#F26A1B";

export const TAGLINE_FONT_FAMILY: Record<TaglineFont, string> = {
  script: "'Kalam', 'Comic Sans MS', cursive",
  bold: "'Poppins', 'Helvetica Neue', Arial, sans-serif",
  serif: "Georgia, 'Times New Roman', serif",
};

const SANS = "'Poppins', 'Helvetica Neue', Arial, sans-serif";

/** Loads the Kalam handwriting font once, for the script tagline. */
const useScriptFont = () => {
  React.useEffect(() => {
    const id = "card-studio-kalam-font";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Kalam:wght@400;700&display=swap";
    document.head.appendChild(link);
  }, []);
};

const fadeFor = (style: CardStyle) => {
  const s = style.overlayStrength;
  if (style.overlay === "light") {
    return `linear-gradient(90deg, rgba(255,247,240,${s}) 0%, rgba(255,247,240,${(s * 0.85).toFixed(3)}) 38%, rgba(255,247,240,0) 72%)`;
  }
  const a = s * 0.85;
  return `linear-gradient(90deg, rgba(0,0,0,${a.toFixed(3)}) 0%, rgba(0,0,0,${(a * 0.7).toFixed(3)}) 40%, rgba(0,0,0,0) 75%)`;
};

/** Rolls through the photos when rolling is on. */
const useRollingIndex = (count: number, rolling: boolean, intervalSec: number) => {
  const [i, setI] = React.useState(0);
  React.useEffect(() => {
    setI(0);
    if (!rolling || count < 2) return;
    const t = window.setInterval(() => setI((x) => (x + 1) % count), Math.max(2, intervalSec) * 1000);
    return () => window.clearInterval(t);
  }, [count, rolling, intervalSec]);
  return count ? i % count : 0;
};

const Photo: React.FC<{ photos: string[]; style: CardStyle; height: number | string }> = ({ photos, style, height }) => {
  const idx = useRollingIndex(photos.length, style.photos.rolling, style.photos.intervalSec);
  if (!photos.length) {
    return <div style={{ position: "absolute", inset: 0, height, background: FOOD_PLACEHOLDER }} />;
  }
  return (
    <div style={{ position: "absolute", inset: 0, height, overflow: "hidden", background: "#2a1a10" }}>
      {photos.map((src, i) => (
        <img
          key={`${src}-${i}`}
          src={src}
          alt=""
          draggable={false}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: i === idx ? 1 : 0,
            transition: "opacity 600ms ease",
          }}
        />
      ))}
      {photos.length > 1 && style.photos.rolling && (
        <div style={{ position: "absolute", bottom: 6, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 4 }}>
          {photos.map((_, i) => (
            <span
              key={i}
              style={{
                width: i === idx ? 8 : 5,
                height: 5,
                borderRadius: 3,
                background: i === idx ? "#FFFFFF" : "rgba(255,255,255,0.55)",
                transition: "width 200ms",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const CircleButton: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    style={{
      width: 30,
      height: 30,
      borderRadius: "50%",
      background: "#FFFFFF",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
      color: "#1A1A1A",
    }}
  >
    {children}
  </span>
);

const Badge: React.FC<{ badge: NonNullable<CardPreviewData["badge"]> }> = ({ badge }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 3,
      background: badge.color,
      color: "#FFFFFF",
      borderRadius: 999,
      padding: "3px 9px 3px 6px",
      fontFamily: SANS,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.2,
      lineHeight: "16px",
      whiteSpace: "nowrap",
      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
    }}
  >
    <CardIcon name={badge.icon} style={{ fontSize: 13 }} />
    {badge.text}
  </span>
);

const TimePill: React.FC<{ time: string; colors: CardColors }> = ({ time, colors }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      background: colors.timeBg,
      color: colors.timeText,
      borderRadius: 999,
      padding: "4px 10px",
      fontFamily: SANS,
      fontSize: 11.5,
      fontWeight: 700,
      letterSpacing: 0.2,
      whiteSpace: "nowrap",
      boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
    }}
  >
    <AccessTimeIcon style={{ fontSize: 13 }} />
    {time}
  </span>
);

/** Name, rating, distance, cuisines and chips. */
const Details: React.FC<{
  data: CardPreviewData;
  style: CardStyle;
  colors: CardColors;
  showChips: boolean;
  compact?: boolean;
}> = ({ data, style, colors, showChips, compact }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: compact ? 3 : 4, minWidth: 0 }}>
    <div
      style={{
        fontFamily: SANS,
        fontSize: compact ? 18 : 21,
        fontWeight: style.nameWeight,
        color: colors.name,
        letterSpacing: 0.2,
        lineHeight: 1.15,
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
      }}
    >
      {data.name}
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 2,
          background: colors.ratingBg,
          color: "#FFFFFF",
          borderRadius: 999,
          padding: "1px 7px 1px 6px",
          fontFamily: SANS,
          fontSize: 11.5,
          fontWeight: 700,
          letterSpacing: 0.2,
        }}
      >
        <StarIcon style={{ fontSize: 12 }} />
        {data.rating.toFixed(1)}
      </span>
      <span style={{ fontFamily: SANS, fontSize: 11.5, color: colors.meta, letterSpacing: 0.2 }}>({data.reviews})</span>
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 2, fontFamily: SANS, fontSize: 11.5, color: colors.meta, letterSpacing: 0.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
      <PlaceOutlinedIcon style={{ fontSize: 13 }} />
      {data.distance} · {data.area}
    </div>
    <div style={{ fontFamily: SANS, fontSize: 11.5, color: colors.meta, letterSpacing: 0.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
      {data.cuisines}
    </div>
    {showChips && data.chips.length > 0 && (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 2 }}>
        {data.chips.slice(0, 3).map((c, i) => (
          <span
            key={`${c.key}-${i}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
              background: colors.chipBg,
              color: colors.chipText,
              borderRadius: 999,
              padding: "2px 7px 2px 5px",
              fontFamily: SANS,
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: 0.2,
              whiteSpace: "nowrap",
              border: "1px solid rgba(255,255,255,0.18)",
            }}
          >
            <CardIcon name={c.icon} style={{ fontSize: 11 }} />
            {c.label}
          </span>
        ))}
      </div>
    )}
  </div>
);

/**
 * One restaurant card as the buyer app draws it, at phone width.
 * Pass onTaglineMove to let the admin drag the tagline (x / y, 0..1 of the card).
 */
export const CardPreview: React.FC<{
  style: CardStyle;
  data: CardPreviewData;
  width?: number;
  onTaglineMove?: (x: number, y: number) => void;
}> = ({ style, data, width = 360, onTaglineMove }) => {
  useScriptFont();
  const cardRef = React.useRef<HTMLDivElement>(null);
  const dragging = React.useRef(false);

  const layout = style.layout;
  const pos = style.positions;
  // Split and classic put the text on a light panel: they keep the admin's
  // colours only when the style itself is light, so text never disappears.
  const colors: CardColors = layout === "overlay" || style.overlay === "light" ? style.colors : LIGHT_CARD_COLORS;
  const photoH = layout === "classic" ? Math.round(style.height * 0.62) : style.height;

  const moveTo = (e: React.PointerEvent) => {
    const r = cardRef.current?.getBoundingClientRect();
    if (!r || !onTaglineMove) return;
    const clamp = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 100) / 100;
    onTaglineMove(clamp((e.clientX - r.left) / r.width), clamp((e.clientY - r.top) / r.height));
  };

  const tagline = data.tagline && data.tagline.text ? data.tagline : null;
  const taglineEl = tagline && (
    <div
      onPointerDown={
        onTaglineMove
          ? (e) => {
              e.preventDefault();
              dragging.current = true;
              (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
            }
          : undefined
      }
      onPointerMove={onTaglineMove ? (e) => dragging.current && moveTo(e) : undefined}
      onPointerUp={onTaglineMove ? () => (dragging.current = false) : undefined}
      onPointerCancel={onTaglineMove ? () => (dragging.current = false) : undefined}
      title={onTaglineMove ? "Drag to move the tagline" : undefined}
      style={{
        position: "absolute",
        left: `${tagline.x * 100}%`,
        top: `${tagline.y * 100}%`,
        transform: `translate(-50%, -50%) rotate(${tagline.rotate}deg)`,
        fontFamily: TAGLINE_FONT_FAMILY[tagline.font] ?? TAGLINE_FONT_FAMILY.script,
        fontWeight: tagline.font === "bold" ? 800 : tagline.font === "script" ? 700 : 600,
        fontStyle: tagline.font === "serif" ? "italic" : "normal",
        fontSize: 19 * tagline.size,
        lineHeight: 1.1,
        letterSpacing: 0.3,
        color: tagline.color,
        textShadow: "0 2px 6px rgba(0,0,0,0.45)",
        maxWidth: width * 0.55,
        textAlign: "center",
        whiteSpace: "normal",
        zIndex: 4,
        cursor: onTaglineMove ? "grab" : "default",
        touchAction: "none",
        userSelect: "none",
        outline: onTaglineMove ? "1px dashed rgba(255,255,255,0.7)" : undefined,
        outlineOffset: 3,
        borderRadius: 4,
      }}
    >
      {tagline.text}
    </div>
  );

  const buttons = (
    <div style={{ position: "absolute", top: 10, right: 10, display: "flex", gap: 6, zIndex: 5 }}>
      <CircleButton>
        <FavoriteBorderIcon style={{ fontSize: 17 }} />
      </CircleButton>
      <CircleButton>
        <MoreVertIcon style={{ fontSize: 17 }} />
      </CircleButton>
    </div>
  );

  const badgeEl =
    data.badge && pos.badge !== "hidden" ? (
      <div
        style={{
          position: "absolute",
          top: 12,
          zIndex: 5,
          ...(pos.badge === "top-right" ? { right: 84 } : { left: 12 }),
        }}
      >
        <Badge badge={data.badge} />
      </div>
    ) : null;

  const timeEl =
    pos.time !== "hidden" ? (
      <div
        style={{
          position: "absolute",
          zIndex: 5,
          right: 10,
          ...(pos.time === "top-right" ? { top: 48 } : { bottom: layout === "classic" ? undefined : 10, top: layout === "classic" ? photoH - 36 : undefined }),
        }}
      >
        <TimePill time={data.time} colors={style.colors} />
      </div>
    ) : null;

  const frame: React.CSSProperties = {
    position: "relative",
    width,
    borderRadius: style.radius,
    overflow: "hidden",
    border: style.borderColor && style.borderWidth > 0 ? `${style.borderWidth}px solid ${style.borderColor}` : undefined,
    boxShadow: style.shadow ? "0 6px 18px rgba(0,0,0,0.16)" : undefined,
    background: layout === "split" ? CREAM : "#FFFFFF",
    boxSizing: "border-box",
    flexShrink: 0,
  };

  const badgeTopLeft = !!badgeEl && pos.badge === "top-left";
  const justify = pos.name === "top-left" ? "flex-start" : pos.name === "bottom-left" ? "flex-end" : "center";

  if (layout === "classic") {
    return (
      <div ref={cardRef} style={frame}>
        <div style={{ position: "relative", height: photoH }}>
          <Photo photos={data.photos} style={style} height={photoH} />
        </div>
        <div style={{ padding: "10px 14px 12px", background: "#FFFFFF" }}>
          <Details data={data} style={style} colors={colors} showChips={pos.chips} compact />
        </div>
        {buttons}
        {badgeEl}
        {timeEl}
        {taglineEl}
      </div>
    );
  }

  if (layout === "split") {
    const panelW = Math.round(width * 0.5);
    return (
      <div ref={cardRef} style={{ ...frame, height: style.height }}>
        <div style={{ position: "absolute", top: 0, bottom: 0, right: 0, width: width - panelW + 24 }}>
          <Photo photos={data.photos} style={style} height="100%" />
        </div>
        {/* Cream panel with a curved orange edge over the photo. */}
        <svg
          width={panelW + 30}
          height={style.height}
          viewBox={`0 0 ${panelW + 30} 100`}
          preserveAspectRatio="none"
          style={{ position: "absolute", left: 0, top: 0, zIndex: 1 }}
        >
          <path
            d={`M0,0 L${panelW + 4},0 C${panelW - 26},35 ${panelW + 34},65 ${panelW - 2},100 L0,100 Z`}
            fill={CREAM}
          />
          <path
            d={`M${panelW + 4},0 C${panelW - 26},35 ${panelW + 34},65 ${panelW - 2},100`}
            fill="none"
            stroke={ORANGE}
            strokeWidth={3}
            vectorEffect="non-scaling-stroke"
          />
        </svg>
        <div
          style={{
            position: "absolute",
            zIndex: 2,
            left: 0,
            top: 0,
            bottom: 0,
            width: panelW - 8,
            padding: `${badgeTopLeft ? 44 : 14}px 10px 14px 14px`,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            justifyContent: justify,
          }}
        >
          <Details data={data} style={style} colors={colors} showChips={pos.chips} compact />
        </div>
        {buttons}
        {badgeEl}
        {timeEl}
        {taglineEl}
      </div>
    );
  }

  // overlay
  return (
    <div ref={cardRef} style={{ ...frame, height: style.height }}>
      <Photo photos={data.photos} style={style} height="100%" />
      <div style={{ position: "absolute", inset: 0, background: fadeFor(style), zIndex: 1 }} />
      <div
        style={{
          position: "absolute",
          zIndex: 2,
          left: 0,
          top: 0,
          bottom: 0,
          width: "64%",
          padding: `${badgeTopLeft ? 42 : 14}px 8px 14px 14px`,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          justifyContent: justify,
        }}
      >
        <Details data={data} style={style} colors={colors} showChips={pos.chips} />
      </div>
      {buttons}
      {badgeEl}
      {timeEl}
      {taglineEl}
    </div>
  );
};

export default CardPreview;
